import type { AdminResetPasswordResponse } from '@/types/api.types'

/** Copy shown before an admin sends a password reset. */
export function adminPasswordResetConfirmMessage(name: string): string {
  return `Email "${name}" a one-time link to set a new password? They will be signed out of every session now. Their current password is not changed; the link works once and expires in 24 hours. If they have not accepted their invitation yet, the invitation is sent again instead.`
}

/** The toast after an admin reset, by what was sent and whether email went out. */
export function adminPasswordResetOutcome(result: AdminResetPasswordResponse): {
  level: 'success' | 'warning'
  message: string
} {
  if (result.delivery === 'invite') {
    return result.emailDispatched
      ? { level: 'success', message: 'Invitation sent again' }
      : {
          level: 'warning',
          message: 'Invitation renewed, but email sending is not available right now.',
        }
  }
  return result.emailDispatched
    ? { level: 'success', message: 'Password reset link sent. They have been signed out.' }
    : {
        level: 'warning',
        message:
          'They have been signed out and a reset link was created, but email sending is not available right now.',
      }
}

/** Characters that cannot be misread aloud or by hand: no 0/O, 1/l/I, 2/Z, 5/S, 8/B. */
const LOWER = 'abcdefghjkmnpqrstuvwxyz'
const UPPER = 'ACDEFGHJKLMNPQRTUVWXY'
const DIGIT = '34679'
const ALL = LOWER + UPPER + DIGIT

/** Uniform random index below `max` from the browser CSPRNG (rejection sampling, no bias). */
function randomIndex(max: number): number {
  const limit = Math.floor(0x1_0000_0000 / max) * max
  const buffer = new Uint32Array(1)
  do {
    crypto.getRandomValues(buffer)
  } while (buffer[0] >= limit)
  return buffer[0] % max
}

function randomChar(from: string): string {
  return from[randomIndex(from.length)]
}

/**
 * A random temporary password in three groups of four (`Kp7m-Xq4r-Tz9w`):
 * about 67 bits of entropy, easy to read out over the phone, and well inside
 * the 8 to 72 character policy. It always holds an uppercase letter, a
 * lowercase letter and a digit, so it passes stricter rules too.
 */
export function generateTemporaryPassword(): string {
  for (;;) {
    const groups = Array.from({ length: 3 }, () =>
      Array.from({ length: 4 }, () => randomChar(ALL)).join('')
    )
    const password = groups.join('-')
    if (/[a-z]/.test(password) && /[A-Z]/.test(password) && /[0-9]/.test(password)) {
      return password
    }
  }
}
